/**
 * Thread Filter - Multi-checkbox selector for thread names
 */

import React, { useState, useCallback, useRef, useEffect } from "react";

export interface ThreadFilterProps {
  threads: string[];
  selectedThreads: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  allSelected: boolean;
  onAllSelectedChange: (allSelected: boolean) => void;
}

export function ThreadFilter(props: ThreadFilterProps) {
  const { threads, selectedThreads, onSelectionChange, allSelected, onAllSelectedChange } = props;
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = useCallback((thread: string) => {
    const newSelected = new Set(selectedThreads);
    if (newSelected.has(thread)) {
      newSelected.delete(thread);
    } else {
      newSelected.add(thread);
    }
    onSelectionChange(newSelected);
    // If we deselected something, clear the "all selected" flag
    if (newSelected.has(thread)) {
      // We just added it, check if we now have everything
      if (newSelected.size === threads.length) {
        onAllSelectedChange(true);
      }
    } else {
      // We just removed it, clear the flag
      onAllSelectedChange(false);
    }
  }, [selectedThreads, onSelectionChange, onAllSelectedChange, threads]);

  const handleSelectAll = useCallback(() => {
    onSelectionChange(new Set(threads));
    onAllSelectedChange(true);
  }, [threads, onSelectionChange, onAllSelectedChange]);

  const handleClearAll = useCallback(() => {
    onSelectionChange(new Set());
    onAllSelectedChange(false);
  }, [onSelectionChange, onAllSelectedChange]);

  const selectedCount = selectedThreads.size;
  const totalCount = threads.length;
  const buttonLabel = allSelected
    ? "Threads: All"
    : selectedCount === 0
    ? "Threads: None"
    : `Threads: ${selectedCount}/${totalCount}`;

  // Create tooltip showing selected values
  const tooltipText = allSelected
    ? "All threads selected"
    : selectedCount === 0
    ? "No threads selected"
    : `Selected: ${Array.from(selectedThreads).sort().join(', ')}`;

  if (threads.length === 0) {
    return null; // Don't show if no threads
  }

  return (
    <div className="thread-filter" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="thread-filter__button"
        title={tooltipText}
      >
        {buttonLabel}
      </button>

      {isOpen && (
        <>
          <div className="thread-filter__backdrop" onClick={() => setIsOpen(false)} />
          <div className="thread-filter__menu">
            <div className="thread-filter__header">
              <span>Thread Names</span>
              <div className="thread-filter__actions">
                <button onClick={handleSelectAll} className="thread-filter__action">
                  All
                </button>
                <button onClick={handleClearAll} className="thread-filter__action">
                  None
                </button>
              </div>
            </div>
            <div className="thread-filter__items">
              {threads.map(thread => (
                <label key={thread} className="thread-filter__item">
                  <input
                    type="checkbox"
                    checked={selectedThreads.has(thread)}
                    onChange={() => handleToggle(thread)}
                  />
                  <span>{thread}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
